import type { PublicEvent, SiteUserRole } from '../types';

export const siteUserRoles: SiteUserRole[] = [
  'admin',
  'staff',
  'board_member',
  'parent',
  'volunteer',
  'donor',
  'vendor',
  'resource_vendor',
  'adult_model',
  'read_only',
];

export interface PortalRoleConfig {
  label: string;
  headline: string;
  summary: string;
  primaryActionLabel: string;
  primaryActionHref: string;
  secondaryActionLabel: string;
  secondaryActionHref: string;
  focusAreas: string[];
  permissions: string[];
  supportNote: string;
}

export const portalRoleConfigs: Record<SiteUserRole, PortalRoleConfig> = {
  admin: {
    label: 'Admin',
    headline: 'Oversee events, access, and operational decisions from one portal.',
    summary: 'Admin access is intended for the project owner or trusted leadership managing event publishing, portal access, and site operations.',
    primaryActionLabel: 'Review event publishing',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'Open account settings',
    secondaryActionHref: '/portal/account',
    focusAreas: ['Create and publish events with ticket pricing', 'Manage site-user access and project roles', 'Review supporter, vendor, and family-facing flows'],
    permissions: ['Full portal visibility for event and access management', 'Role-aware site user administration', 'Project-level operational review before live Bliss auth is connected'],
    supportNote: 'Admin access should stay limited to trusted project leadership once production authentication is wired.',
  },
  staff: {
    label: 'Staff',
    headline: 'Keep programs, event execution, and family support organized.',
    summary: 'Staff access is designed for day-to-day operations, family communication, and internal follow-through around programming and events.',
    primaryActionLabel: 'See event operations',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'View your account',
    secondaryActionHref: '/portal/account',
    focusAreas: ['Track event readiness and current participation types', 'Coordinate volunteer and family follow-up', 'Keep portal communication and logistics organized'],
    permissions: ['Operational visibility into active events', 'Internal access to support workflows and reminders', 'Shared staff-facing workspace with a calmer UI than the public site'],
    supportNote: 'Staff access can later connect to form submissions, event registrations, and internal notes once backend workflows exist.',
  },
  board_member: {
    label: 'Board Member',
    headline: 'Stay close to the mission and keep the public board roster current.',
    summary: 'Board access is meant for leadership visibility, fundraising alignment, and direct ownership of each board member’s public bio and photo.',
    primaryActionLabel: 'Manage your board bio',
    primaryActionHref: '/portal/board',
    secondaryActionLabel: 'View current events',
    secondaryActionHref: '/portal/events',
    focusAreas: ['Keep your public biography and photo current', 'Monitor featured events and fundraising visibility', 'Stay aligned on the public story and supporter flow'],
    permissions: ['Leadership-level visibility into published portal areas', 'Board-member editing for your own public profile', 'A simple workspace for mission and governance review'],
    supportNote: 'Board members can now maintain their own public profile while admins keep the full roster in order.',
  },
  parent: {
    label: 'Parent',
    headline: 'Give families one welcoming place to stay connected and informed.',
    summary: 'Parent access is aimed at event updates, family-ready next steps, and a future home for registrations, resources, and communication.',
    primaryActionLabel: 'See family events',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'Manage your access',
    secondaryActionHref: '/portal/account',
    focusAreas: ['See what is open for family participation', 'Understand upcoming events and community opportunities', 'Prepare for future parent dashboards and intake flows'],
    permissions: ['Family-facing event visibility in a protected area', 'Future access to intake, registration, and resource updates', 'A gentler logged-in experience than a generic portal'],
    supportNote: 'Parent access should remain warm and minimal. It should never feel like enterprise software.',
  },
  volunteer: {
    label: 'Volunteer',
    headline: 'Make volunteer coordination clear before event day gets busy.',
    summary: 'Volunteer access is for team members helping with events, support tasks, and mission-centered service opportunities.',
    primaryActionLabel: 'Review event support needs',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'Update your portal access',
    secondaryActionHref: '/portal/account',
    focusAreas: ['See where volunteer help is connected to active events', 'Keep schedules and expectations easier to scan', 'Prepare for future volunteer intake and assignment tools'],
    permissions: ['Volunteer-specific visibility into active event opportunities', 'A protected workspace for nonprofit service coordination', 'Future-ready foundation for volunteer workflows'],
    supportNote: 'Volunteer experiences should stay simple and confidence-building, especially for first-time helpers.',
  },
  donor: {
    label: 'Donor',
    headline: 'Give supporters a cleaner relationship to the mission after they sign in.',
    summary: 'Donor access is intended for supporters who need a more personal entry point into updates, giving, and event visibility.',
    primaryActionLabel: 'View mission events',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'See your access details',
    secondaryActionHref: '/portal/account',
    focusAreas: ['Connect giving with current mission activity', 'See featured events and support opportunities', 'Prepare for future donor history and giving tools'],
    permissions: ['A supporter-facing portal surface without admin clutter', 'Future room for donor acknowledgements and receipts', 'Clearer continuity between public giving and logged-in access'],
    supportNote: 'Donor access should feel grateful, trustworthy, and restrained rather than transactional or overwhelming.',
  },
  vendor: {
    label: 'Vendor',
    headline: 'Keep vendor participation tied to the event instead of scattered forms.',
    summary: 'Vendor access is focused on event-specific participation, current pricing, and a cleaner path into the public event flow.',
    primaryActionLabel: 'Review vendor event access',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'Check your account',
    secondaryActionHref: '/portal/account',
    focusAreas: ['See active events with vendor participation attached', 'Keep vendor information connected to the right event page', 'Prepare for future application and payment workflows'],
    permissions: ['Vendor-facing visibility into event participation', 'Protected access for future vendor status and forms', 'A clearer path than separate vendor ticket pages'],
    supportNote: 'Vendor access should make the event process feel structured and professional without becoming a complicated dashboard.',
  },
  resource_vendor: {
    label: 'Resource Vendor',
    headline: 'Give community partners one focused place to join the right event.',
    summary: 'Resource vendor access is meant for community organizations and service partners who need a cleaner way to connect with families through events.',
    primaryActionLabel: 'See resource vendor events',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'View your account',
    secondaryActionHref: '/portal/account',
    focusAreas: ['Stay aligned with events that welcome community resources', 'Prepare for future application and booth details', 'Keep partner participation clear and mission-centered'],
    permissions: ['Resource-vendor visibility tied to the event model', 'A future home for partnership forms and event notes', 'Cleaner access than scattered public registration pages'],
    supportNote: 'Resource vendor access should feel mission-aligned and helpful, not like a retail checkout flow.',
  },
  adult_model: {
    label: 'Adult Model',
    headline: 'Apply for event participation and keep model details in one place.',
    summary: 'Adult model access is designed for event-specific applications, profile updates, and approval follow-up without forcing a payment workflow.',
    primaryActionLabel: 'Review model opportunities',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'Update your model profile',
    secondaryActionHref: '/portal/account',
    focusAreas: ['Apply to the right event from one protected flow', 'Keep profile details ready for future event applications', 'Receive approval updates without emailing forms back and forth'],
    permissions: ['Protected access to adult model application flows', 'A reusable profile for future model opportunities', 'Clear separation from ticket checkout and vendor registration'],
    supportNote: 'Adult model access should feel simple, respectful, and application-first rather than transactional.',
  },
  read_only: {
    label: 'Read only',
    headline: 'Offer a protected, non-editing view into the project’s active work.',
    summary: 'Read-only access is useful for observers, advisors, or limited-access users who only need to see current information.',
    primaryActionLabel: 'View event overview',
    primaryActionHref: '/portal/events',
    secondaryActionLabel: 'See account details',
    secondaryActionHref: '/portal/account',
    focusAreas: ['View active events without editing controls', 'Review mission-facing updates in one place', 'Support future light-access stakeholders'],
    permissions: ['Protected visibility without editing authority', 'A simpler oversight experience for limited-access users', 'Clear separation from admin and staff operations'],
    supportNote: 'Read-only access should feel dependable and quiet, with no ambiguity about what can and cannot be changed.',
  },
};

export function isSiteUserRole(value: string): value is SiteUserRole {
  return siteUserRoles.includes(value as SiteUserRole);
}

export function normalizeSiteUserRole(role: string | null | undefined, fallback: SiteUserRole = 'read_only'): SiteUserRole {
  const normalized = role?.trim().toLowerCase();
  return normalized && isSiteUserRole(normalized) ? normalized : fallback;
}

export function getSiteRoleLabel(role: SiteUserRole): string {
  return portalRoleConfigs[role].label;
}

export function getPortalRoleConfig(role: SiteUserRole): PortalRoleConfig {
  return portalRoleConfigs[role];
}

export function getPortalEventActionLabel(role: SiteUserRole, event: PublicEvent): string {
  switch (role) {
    case 'admin':
      return `Review ${event.title}`;
    case 'staff':
      return 'Open event operations';
    case 'board_member':
      return 'Review event snapshot';
    case 'parent':
      return 'See family-facing event details';
    case 'volunteer':
      return 'See volunteer-ready details';
    case 'donor':
      return 'See supporter-facing event details';
    case 'vendor':
      return 'Review vendor participation';
    case 'resource_vendor':
      return 'Review partner participation';
    case 'adult_model':
      return 'Review model application details';
    case 'read_only':
      return 'Open event overview';
    default:
      return 'View event details';
  }
}
